import { Pagination } from '@/constants/pagination';
import {
  CashbackAccrualItem,
  CashbackRedemptionItem,
  CashbackSummaryContent,
  CardDetailContent,
  CardSummaryContent,
  GroupCardsContent,
  NotificationHistoryItem,
  NotificationSummaryContent,
  OverviewContent,
  SubCustomerActivityItem,
  SubCustomerItem,
  SubCustomerSummaryContent,
  TransactionDetailContent,
  TransactionItem,
  TransactionSummaryContent,
  VirtualAccountSummaryContent,
} from '@/types/customer-details';
import { PaginationAPI } from '@/types/pagination';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type CustomerDetailsState = {
  overview: OverviewContent | null;
  subCustomerSummary: SubCustomerSummaryContent | null;
  subCustomers: {
    data: SubCustomerItem[];
    pagination: PaginationAPI;
  };
  subCustomerActivity: {
    data: SubCustomerActivityItem[];
    pagination: PaginationAPI;
  };
  virtualAccountSummary: VirtualAccountSummaryContent | null;
  cardSummary: CardSummaryContent | null;
  cardDetail: CardDetailContent | null;
  groupCards: GroupCardsContent | null;
  transactionSummary: TransactionSummaryContent | null;
  transactions: {
    data: TransactionItem[];
    pagination: PaginationAPI;
  };
  transactionDetail: TransactionDetailContent | null;
  notificationSummary: NotificationSummaryContent | null;
  notificationHistory: {
    data: NotificationHistoryItem[];
    pagination: PaginationAPI;
  };
  cashbackSummary: CashbackSummaryContent | null;
  cashbackAccruals: {
    data: CashbackAccrualItem[];
    pagination: PaginationAPI;
  };
  cashbackRedemptions: {
    data: CashbackRedemptionItem[];
    pagination: PaginationAPI;
  };
};

const defaultPagination: PaginationAPI = {
  Page: Pagination.Page,
  totalPages: 0,
  totalElements: 0,
  Size: Pagination.Size,
};

export const customerDetailsInitialState: CustomerDetailsState = {
  overview: null,
  subCustomerSummary: null,
  subCustomers: { data: [], pagination: { ...defaultPagination } },
  subCustomerActivity: { data: [], pagination: { ...defaultPagination } },
  virtualAccountSummary: null,
  cardSummary: null,
  cardDetail: null,
  groupCards: null,
  transactionSummary: null,
  transactions: { data: [], pagination: { ...defaultPagination } },
  transactionDetail: null,
  notificationSummary: null,
  notificationHistory: { data: [], pagination: { ...defaultPagination } },
  cashbackSummary: null,
  cashbackAccruals: { data: [], pagination: { ...defaultPagination } },
  cashbackRedemptions: { data: [], pagination: { ...defaultPagination } },
};

const slice = createSlice({
  name: 'customerDetails',
  initialState: customerDetailsInitialState,
  reducers: {
    setOverviewAction(state, { payload }: PayloadAction<OverviewContent>) {
      state.overview = payload;
    },
    setSubCustomerSummaryAction(state, { payload }: PayloadAction<SubCustomerSummaryContent>) {
      state.subCustomerSummary = payload;
    },
    setSubCustomersAction(
      state,
      action: PayloadAction<{
        data: SubCustomerItem[];
        pagination?: PaginationAPI;
        append?: boolean;
      }>,
    ) {
      const { payload } = action;
      if (payload.append) {
        state.subCustomers.data = [...state.subCustomers.data, ...payload.data];
      } else {
        state.subCustomers.data = payload.data;
      }
      state.subCustomers.pagination = payload.pagination || state.subCustomers.pagination;
    },
    setSubCustomerActivityAction(
      state,
      action: PayloadAction<{
        data: SubCustomerActivityItem[];
        pagination?: PaginationAPI;
        append?: boolean;
      }>,
    ) {
      const { payload } = action;
      if (payload.append) {
        state.subCustomerActivity.data = [...state.subCustomerActivity.data, ...payload.data];
      } else {
        state.subCustomerActivity.data = payload.data;
      }
      state.subCustomerActivity.pagination =
        payload.pagination || state.subCustomerActivity.pagination;
    },
    setVirtualAccountSummaryAction(
      state,
      { payload }: PayloadAction<VirtualAccountSummaryContent>,
    ) {
      state.virtualAccountSummary = payload;
    },
    setCardSummaryAction(state, { payload }: PayloadAction<CardSummaryContent>) {
      state.cardSummary = payload;
    },
    setCardDetailAction(state, { payload }: PayloadAction<CardDetailContent>) {
      state.cardDetail = payload;
    },
    clearCardDetailAction(state) {
      state.cardDetail = null;
    },
    setGroupCardsAction(state, { payload }: PayloadAction<GroupCardsContent>) {
      state.groupCards = payload;
    },
    setTransactionSummaryAction(state, { payload }: PayloadAction<TransactionSummaryContent>) {
      state.transactionSummary = payload;
    },
    setTransactionsAction(
      state,
      action: PayloadAction<{
        data: TransactionItem[];
        pagination?: PaginationAPI;
        append?: boolean;
      }>,
    ) {
      const { payload } = action;
      if (payload.append) {
        state.transactions.data = [...state.transactions.data, ...payload.data];
      } else {
        state.transactions.data = payload.data;
      }
      state.transactions.pagination = payload.pagination || state.transactions.pagination;
    },
    setTransactionDetailAction(state, { payload }: PayloadAction<TransactionDetailContent>) {
      state.transactionDetail = payload;
    },
    clearTransactionDetailAction(state) {
      state.transactionDetail = null;
    },
    setNotificationSummaryAction(state, { payload }: PayloadAction<NotificationSummaryContent>) {
      state.notificationSummary = payload;
    },
    setNotificationHistoryAction(
      state,
      action: PayloadAction<{
        data: NotificationHistoryItem[];
        pagination?: PaginationAPI;
        append?: boolean;
      }>,
    ) {
      const { payload } = action;
      if (payload.append) {
        state.notificationHistory.data = [...state.notificationHistory.data, ...payload.data];
      } else {
        state.notificationHistory.data = payload.data;
      }
      state.notificationHistory.pagination =
        payload.pagination || state.notificationHistory.pagination;
    },
    setCashbackSummaryAction(state, { payload }: PayloadAction<CashbackSummaryContent>) {
      state.cashbackSummary = payload;
    },
    setCashbackAccrualsAction(
      state,
      action: PayloadAction<{
        data: CashbackAccrualItem[];
        pagination?: PaginationAPI;
        append?: boolean;
      }>,
    ) {
      const { payload } = action;
      if (payload.append) {
        state.cashbackAccruals.data = [...state.cashbackAccruals.data, ...payload.data];
      } else {
        state.cashbackAccruals.data = payload.data;
      }
      state.cashbackAccruals.pagination = payload.pagination || state.cashbackAccruals.pagination;
    },
    setCashbackRedemptionsAction(
      state,
      action: PayloadAction<{
        data: CashbackRedemptionItem[];
        pagination?: PaginationAPI;
        append?: boolean;
      }>,
    ) {
      const { payload } = action;
      if (payload.append) {
        state.cashbackRedemptions.data = [...state.cashbackRedemptions.data, ...payload.data];
      } else {
        state.cashbackRedemptions.data = payload.data;
      }
      state.cashbackRedemptions.pagination =
        payload.pagination || state.cashbackRedemptions.pagination;
    },
    resetCustomerDetailsAction() {
      return customerDetailsInitialState;
    },
  },
});

export const {
  setOverviewAction,
  setSubCustomerSummaryAction,
  setSubCustomersAction,
  setSubCustomerActivityAction,
  setVirtualAccountSummaryAction,
  setCardSummaryAction,
  setCardDetailAction,
  clearCardDetailAction,
  setGroupCardsAction,
  setTransactionSummaryAction,
  setTransactionsAction,
  setTransactionDetailAction,
  clearTransactionDetailAction,
  setNotificationSummaryAction,
  setNotificationHistoryAction,
  setCashbackSummaryAction,
  setCashbackAccrualsAction,
  setCashbackRedemptionsAction,
  resetCustomerDetailsAction,
} = slice.actions;

export default slice.reducer;
